import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/modal";
import { Button, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import React from "react";

interface DeleteSegmentModalProps {
  isOpen: boolean;
  segmentName: string;
  scheduledCount: number;
  draftCount: number;
  isLoading: boolean;
  isBlocked: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export const notificationCountLabel = (
  count: number,
  status: "Scheduled" | "Draft"
): string => {
  const label = status === "Scheduled" ? "Scheduled" : "Draft";
  return count === 1
    ? `1 ${label} notification`
    : `${count} ${label} notifications`;
};

export const DeleteSegmentModal = ({
  isOpen,
  segmentName,
  scheduledCount,
  draftCount,
  isLoading,
  isBlocked,
  onConfirm,
  onClose,
}: DeleteSegmentModalProps) => {
  const headerBg = isBlocked ? "orange.600" : "cherryButton.600";

  const renderBody = () => {
    if (isLoading) {
      return (
        <HStack justify="center" py={6}>
          <Spinner size="md" />
        </HStack>
      );
    }

    if (isBlocked) {
      return (
        <VStack align="start" spacing={3}>
          <Text>
            This segment is currently used in upcoming notifications and cannot
            be deleted.
          </Text>
          <VStack align="start" spacing={1} pl={2}>
            {scheduledCount > 0 && (
              <Text>
                &bull; {notificationCountLabel(scheduledCount, "Scheduled")}
              </Text>
            )}
            {draftCount > 0 && (
              <Text>&bull; {notificationCountLabel(draftCount, "Draft")}</Text>
            )}
          </VStack>
        </VStack>
      );
    }

    return (
      <VStack align="start" spacing={3}>
        <Text>Are you sure you want to delete this saved segment?</Text>
        <VStack align="start" spacing={1}>
          <Text fontWeight="semibold">Segment Name:</Text>
          <Text>{segmentName}</Text>
        </VStack>
      </VStack>
    );
  };

  const renderFooter = () => {
    if (isBlocked) {
      return (
        <Button onClick={onClose} disabled={isLoading}>
          Close
        </Button>
      );
    }

    return (
      <>
        <Button onClick={onClose} disabled={isLoading} mr={3}>
          Cancel
        </Button>
        <Button
          colorScheme="red"
          onClick={onConfirm}
          isLoading={isLoading}
          disabled={isLoading}
        >
          Delete
        </Button>
      </>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg={headerBg} color="white" borderTopRadius="md">
          {isBlocked ? "Segment Cannot Be Deleted" : "Delete Saved Segment?"}
        </ModalHeader>
        <ModalBody pt={5} pb={4}>
          {renderBody()}
        </ModalBody>
        <ModalFooter>{renderFooter()}</ModalFooter>
      </ModalContent>
    </Modal>
  );
};
